// src/components/onboarding/onboardingRecord.js
//
// WHERE THE ONBOARDING RECORD LIVES, and how it is read forward.
//
// ⚠️ THE RECORD BELONGS TO THE PROPOSAL, NOT TO A SIGNED CONTRACT.
//
// It used to live on the ContractInstance, and that was wrong in a way nothing
// could surface until a proposal produced TWO signed envelopes. Contracts are
// deliberately separate envelopes — contractModel says so outright: "a proposal
// with two contracts has two instances, two envelopes and two statuses" — and
// ContractsPanel rendered an OnboardingPanel for EVERY instance whose status
// was `signed`. Stage A/B/C stored their idempotency inside that per-instance
// record (`autotaskContractId`, `ticketIds`), so two signed envelopes meant two
// empty records, and therefore:
//
//     two Autotask contracts, and two complete sets of tickets,
//
// against a decision that says there is always exactly ONE contract. Nothing
// would have looked wrong in Autotask, and it could only ever have been
// discovered on a real customer.
//
// Stage D never had the bug, and that is what pointed at the fix:
// `existingOnboardingQuotes` dedupes on **proposalId**, not on the instance, so
// quotes stayed correct throughout. This module puts A/B/C on the same footing.
//
// Kept React-free and import-free so the rules can be unit-tested without
// pulling MSAL in behind a component — the same reason proposalStatus.js and
// opportunityPipeline.js exist as bare modules.

/** Every contract instance belonging to this proposal. */
export function contractsFor(instances = [], proposalId) {
  if (!proposalId) return [];        // an undefined id must not match undefined
  return (instances || []).filter((c) => c && String(c.proposalId) === String(proposalId));
}

/** Those that have actually been signed. */
export function signedContractsFor(instances = [], proposalId) {
  return contractsFor(instances, proposalId).filter((c) => c.status === "signed");
}

/** Is the pipeline available at all? One signature is enough — see signedAtFor. */
export function hasSignedContract(instances = [], proposalId) {
  return signedContractsFor(instances, proposalId).length > 0;
}

/**
 * The signature that dates the contract.
 *
 * ⚠ The EARLIEST signature, not the latest. `contractDates()` turns this into
 * the Autotask contract's start date, and the pipeline runs on the first
 * envelope to come back — so a second envelope signed a fortnight later must
 * not silently redate a contract that already exists. Returns an ISO string
 * because that is what the backend's plan() accepts, or null.
 */
export function signedAtFor(instances = [], proposalId) {
  const stamps = signedContractsFor(instances, proposalId)
    .map((c) => c.signedAt)
    .filter((t) => t != null && !Number.isNaN(new Date(t).getTime()));
  if (!stamps.length) return null;
  return new Date(Math.min(...stamps.map((t) => new Date(t).getTime()))).toISOString();
}

/**
 * The onboarding record for this proposal.
 *
 * READS LEGACY FORWARD. Records written before this change live on the
 * ContractInstance, and there is no migration: `proposal.onboarding` wins when
 * present, and otherwise the first instance carrying one is used. The moment
 * any stage runs, the record is written to the proposal and stays there, so a
 * stale copy left on an instance is shadowed rather than fought with — the same
 * shape as reading `docType` through `docTypeOf()`.
 *
 * ⚠ Returning the FIRST instance record rather than merging several is
 * deliberate. Before the fix a proposal could only have accumulated more than
 * one by running the pipeline twice, which is the very duplication being fixed;
 * merging them would fuse two contract ids into one record and hide it.
 */
export function onboardingRecordOf(proposal, instances = []) {
  if (proposal?.onboarding) return proposal.onboarding;
  const legacy = contractsFor(instances, proposal?.id).find((c) => c.onboarding);
  return legacy ? legacy.onboarding : null;
}

/**
 * True when more than one instance carries its own record — i.e. the duplicate
 * this module exists to prevent has ALREADY happened on stored data.
 *
 * Surfaced rather than silently resolved: two records mean two Autotask
 * contracts, and a human has to decide which is real and deactivate the other.
 */
export function hasSplitLegacyRecords(proposal, instances = []) {
  if (proposal?.onboarding) return false;      // the proposal-level record settles it
  return contractsFor(instances, proposal?.id).filter((c) => c.onboarding).length > 1;
}

/** The patch to apply to the proposal when a stage returns a record. */
export function onboardingPatch(record, companyId) {
  return { onboarding: { ...record, companyID: companyId || null } };
}
